import { Link } from "react-router-dom";
import { ScrollText } from "lucide-react";
import LegalLayout, { LegalSection } from "../Legal/LegalLayout";

const CONTACT_EMAIL = "contato@calriz.com";

function Clause({ n, children }: { n: string; children: React.ReactNode }) {
  return (
    <p>
      <strong>{n}.</strong> {children}
    </p>
  );
}

// O número de cada seção é desenhado pelo LegalLayout (01, 02…); as cláusulas (1.1, 1.2…) seguem essa ordem.
const SECTIONS: LegalSection[] = [
  {
    id: "programa",
    title: "Sobre o Programa",
    content: (
      <>
        <Clause n="1.1">
          O Programa tem caráter exclusivamente educacional e de capacitação profissional. Ele oferece trilhas de
          treinamento e laboratórios práticos em uma plataforma de ensino on-line, um simulado, vouchers para o exame
          de certificação e incentivos aos participantes que cumprirem as regras de cada etapa.
        </Clause>
        <Clause n="1.2">
          A participação é gratuita. Não há taxa de inscrição e não é exigida a compra de nenhum produto ou serviço.
          Nenhuma etapa depende de sorte: vouchers e incentivos são concedidos somente a quem cumprir os critérios
          objetivos deste Regulamento.
        </Clause>
        <Clause n="1.3">
          Os custos de equipamento, acesso à internet e eventual deslocamento necessários para participar ficam por
          conta do participante.
        </Clause>
        <Clause n="1.4">O Programa é realizado no Brasil, em língua portuguesa.</Clause>
      </>
    ),
  },
  {
    id: "participacao",
    title: "Quem pode participar",
    content: (
      <>
        <Clause n="2.1">Pessoas físicas maiores de 18 anos, residentes no Brasil e com CPF regular.</Clause>
        <Clause n="2.2">
          Não podem participar sócios, administradores, empregados e prestadores de serviço da Calriz, nem seus
          cônjuges e parentes até o segundo grau.
        </Clause>
        <Clause n="2.3">
          Cada pessoa pode ter uma única inscrição. A Calriz pode solicitar CPF e documento oficial com foto para
          confirmar a identidade do participante, especialmente antes de conceder o voucher ou pagar o incentivo.
        </Clause>
      </>
    ),
  },
  {
    id: "inscricao",
    title: "Inscrição",
    content: (
      <>
        <Clause n="3.1">
          A inscrição é feita exclusivamente pelo formulário da página do Programa (
          <Link to="/universidade-calriz">calriz.org/universidade-calriz</Link>) e só é concluída após a confirmação do
          e-mail com o código enviado pela Calriz.
        </Clause>
        <Clause n="3.2">
          O participante é responsável pela veracidade dos dados informados e por mantê-los atualizados. A Calriz não
          se responsabiliza por comunicações não recebidas por e-mail incorreto, caixa de entrada cheia ou filtros de
          spam.
        </Clause>
        <Clause n="3.3">
          As vagas são limitadas. A Calriz pode encerrar as inscrições antes do previsto ao atingir o número de vagas
          definido para o Programa.
        </Clause>
        <Clause n="3.4">
          A confirmação da inscrição não garante voucher, incentivo financeiro, estágio ou contratação. Esses
          benefícios dependem do cumprimento das regras de cada etapa.
        </Clause>
      </>
    ),
  },
  {
    id: "treinamento",
    title: "Treinamento na plataforma",
    content: (
      <>
        <Clause n="4.1">
          O período de treinamento vai de <strong>01/12/2026 a 31/01/2027</strong>. As orientações para criar a conta
          na plataforma são enviadas para o e-mail cadastrado.
        </Clause>
        <Clause n="4.2">
          A plataforma de ensino pode ser operada por terceiros. O acesso é pessoal e intransferível, e o participante
          deve seguir os termos de uso da plataforma.
        </Clause>
        <Clause n="4.3">
          A Calriz não se responsabiliza por indisponibilidades, falhas técnicas ou alterações de conteúdo da
          plataforma de terceiros. Se uma indisponibilidade afetar os participantes de forma geral, a Calriz pode
          ajustar os prazos do Programa.
        </Clause>
      </>
    ),
  },
  {
    id: "simulado-voucher",
    title: "Simulado e voucher de certificação",
    content: (
      <>
        <Clause n="5.1">
          O simulado oficial fica disponível na plataforma no final do período de treinamento (final de janeiro de
          2027).
        </Clause>
        <Clause n="5.2">
          Recebem o voucher os <strong>50 (cinquenta) primeiros participantes</strong> que concluírem o simulado e
          atingirem pontuação mínima de <strong>90%</strong>. A ordem é definida pela data e hora de conclusão
          registradas pela plataforma, que valem como critério final de desempate. Se menos de 50 participantes
          atingirem a pontuação mínima, apenas eles recebem o voucher.
        </Clause>
        <Clause n="5.3">
          O simulado é individual. Consultar outras pessoas durante a prova, compartilhar respostas, usar mais de uma
          conta ou cometer qualquer outra fraude desclassifica o participante.
        </Clause>
        <Clause n="5.4">
          O voucher é pessoal e intransferível, cobre apenas a taxa de uma tentativa do exame oficial de certificação e
          não pode ser trocado por dinheiro nem por outro benefício. O prazo de validade é definido pela entidade
          certificadora; voucher não usado dentro do prazo é perdido, sem substituição.
        </Clause>
      </>
    ),
  },
  {
    id: "exame",
    title: "Exame de certificação",
    content: (
      <>
        <Clause n="6.1">
          O exame é aplicado pela entidade certificadora (fabricante da tecnologia ou parceiro autorizado), segundo as
          regras dela de agendamento, identificação, conduta e aprovação. A Calriz não interfere e não responde pelo
          resultado do exame.
        </Clause>
        <Clause n="6.2">
          Custos não cobertos pelo voucher, como reagendamento, nova tentativa ou deslocamento, ficam por conta do
          participante.
        </Clause>
      </>
    ),
  },
  {
    id: "incentivo",
    title: "Vinculação da certificação e incentivo de R$ 1.000,00",
    content: (
      <>
        <Clause n="7.1">
          O participante aprovado no exame deve vincular sua certificação à Calriz durante o mês de{" "}
          <strong>fevereiro de 2027</strong>, seguindo as orientações enviadas por e-mail.
        </Clause>
        <Clause n="7.2">
          A vinculação deve ser mantida ativa por <strong>60 (sessenta) dias corridos</strong>, até abril de 2027.
          Desfazer a vinculação antes do fim desse prazo cancela o direito ao incentivo.
        </Clause>
        <Clause n="7.3">
          Cumpridas as condições, a Calriz paga o incentivo de <strong>R$ 1.000,00 (mil reais)</strong>, uma única vez
          por participante, por PIX ou depósito em conta bancária de titularidade do próprio participante, em até 30
          (trinta) dias após o fim do prazo de manutenção da vinculação.
        </Clause>
        <Clause n="7.4">
          Para receber, o participante deve informar CPF e dados bancários e, se solicitado, assinar recibo. Sobre o
          valor incidem os tributos previstos na legislação, com retenção na fonte quando aplicável.
        </Clause>
        <Clause n="7.5">
          A vinculação da certificação e o recebimento do incentivo não criam vínculo empregatício, societário ou de
          prestação de serviços entre o participante e a Calriz.
        </Clause>
      </>
    ),
  },
  {
    id: "estagio",
    title: "Programa de estágio e carreira",
    content: (
      <>
        <Clause n="8.1">
          Os finalistas poderão ser convidados para o processo seletivo do programa de estágio. O convite, o número de
          vagas e a contratação ficam a critério exclusivo da Calriz, de acordo com o perfil, o desempenho e as
          necessidades da empresa. Concluir o Programa não garante vaga.
        </Clause>
        <Clause n="8.2">
          O estágio segue a Lei nº 11.788/2008: o participante precisa estar regularmente matriculado e com frequência
          em instituição de ensino, e a contratação depende da assinatura do termo de compromisso entre o estagiário,
          a instituição de ensino e a Calriz. Quem não atender a esses requisitos não pode ser contratado como
          estagiário, mas pode ser considerado para outras oportunidades.
        </Clause>
        <Clause n="8.3">
          Quando oferecido, o estágio terá duração mínima de 6 (seis) meses e bolsa de pelo menos R$ 1.500,00 por mês,
          além dos demais direitos previstos em lei. As condições finais constam do termo de compromisso.
        </Clause>
        <Clause n="8.4">
          Dependendo dos conhecimentos e da experiência, o participante poderá receber proposta de contratação
          efetiva, em processo seletivo próprio e segundo a legislação trabalhista.
        </Clause>
      </>
    ),
  },
  {
    id: "desclassificacao",
    title: "Desclassificação",
    content: (
      <>
        <Clause n="9.1">É desclassificado, a qualquer momento, o participante que:</Clause>
        <ul>
          <li>informar dados falsos ou de outra pessoa;</li>
          <li>tiver mais de uma inscrição ou usar mais de uma conta na plataforma;</li>
          <li>cometer fraude no simulado ou no exame, ou usar robôs, scripts e meios automatizados;</li>
          <li>tiver conduta ofensiva ou discriminatória com outros participantes, instrutores ou a equipe da Calriz;</li>
          <li>violar os termos da plataforma de ensino ou da entidade certificadora;</li>
          <li>descumprir qualquer regra deste Regulamento.</li>
        </ul>
        <Clause n="9.2">
          O participante desclassificado perde vouchers e incentivos ainda não usados ou pagos. Se a irregularidade for
          descoberta depois, a Calriz pode cobrar a devolução do valor recebido.
        </Clause>
      </>
    ),
  },
  {
    id: "dados-pessoais",
    title: "Dados pessoais (LGPD)",
    content: (
      <>
        <Clause n="10.1">
          A Calriz trata os dados informados pelo participante (nome, e-mail, telefone e formação e, nas etapas
          seguintes, CPF, dados bancários e informações de desempenho e certificação) para operar o Programa:
          inscrição, comunicações, liberação de acesso, apuração dos resultados, concessão dos vouchers, pagamento do
          incentivo e seleção para estágio e vagas, nos termos da Lei nº 13.709/2018 (LGPD).
        </Clause>
        <Clause n="10.2">
          Os dados podem ser compartilhados com a plataforma de ensino, a entidade certificadora e os prestadores de
          envio de e-mail e de pagamento, somente no necessário para cada etapa.
        </Clause>
        <Clause n="10.3">
          Os dados são mantidos pelo tempo necessário ao Programa e ao cumprimento de obrigações legais. O participante
          pode pedir acesso, correção ou exclusão dos seus dados pelo e-mail <strong>{CONTACT_EMAIL}</strong>; a
          exclusão antes do fim do Programa encerra a participação. Mais detalhes na{" "}
          <Link to="/politica-de-privacidade">Política de Privacidade</Link>.
        </Clause>
      </>
    ),
  },
  {
    id: "nome-e-imagem",
    title: "Divulgação de nome e imagem",
    content: (
      <>
        <Clause n="11.1">
          O participante autoriza, sem ônus, a divulgação do seu nome como contemplado com voucher, incentivo ou estágio
          nos canais da Calriz.
        </Clause>
        <Clause n="11.2">
          O uso de imagem, voz ou depoimento do participante depende de autorização específica, pedida à parte.
        </Clause>
      </>
    ),
  },
  {
    id: "alteracoes",
    title: "Alterações, suspensão e cancelamento",
    content: (
      <>
        <Clause n="12.1">
          A Calriz pode alterar datas, etapas e regras deste Regulamento, ou suspender e cancelar o Programa, em caso de
          força maior, mudança nas regras da plataforma de ensino ou da entidade certificadora, ou por decisão
          interna. As alterações são publicadas nesta página e comunicadas por e-mail aos inscritos.
        </Clause>
        <Clause n="12.2">
          Alterações não prejudicam vouchers já entregues nem incentivos cujas condições já tenham sido cumpridas. Fora
          esses casos, alteração, suspensão ou cancelamento do Programa não geram direito a indenização.
        </Clause>
      </>
    ),
  },
  {
    id: "disposicoes-gerais",
    title: "Disposições gerais",
    content: (
      <>
        <Clause n="13.1">
          Dúvidas e casos não previstos neste Regulamento são resolvidos pela organização do Programa na Calriz.
        </Clause>
        <Clause n="13.2">
          A tolerância da Calriz com o descumprimento de qualquer regra não significa renúncia ao direito de exigi-la.
        </Clause>
        <Clause n="13.3">
          Fica eleito o foro da Comarca de São Paulo/SP para resolver questões relativas a este Regulamento,
          ressalvado o direito do participante de recorrer ao foro do seu domicílio quando a lei assegurar.
        </Clause>
        <Clause n="13.4">
          Contato sobre o Programa: <strong>{CONTACT_EMAIL}</strong>.
        </Clause>
      </>
    ),
  },
];

// /universidade-calriz/regulamento — regulamento aceito no checkbox do formulário de inscrição, no mesmo layout das
// páginas legais. Programa só no Brasil, por isso o texto fica aqui em português e não nos arquivos de tradução.
// Datas e regras: mesmas do TIMELINE de UniversidadeContent.ts.
export default function Regulamento() {
  return (
    <LegalLayout
      seo={{ page: "universidade_regulamento", path: "/universidade-calriz/regulamento" }}
      title="Regulamento da Universidade Calriz"
      icon={ScrollText}
      crumbs={[{ label: "Universidade Calriz", to: "/universidade-calriz" }]}
      intro={
        <p>
          Este Regulamento define as regras de participação no programa Universidade Calriz ("Programa"), promovido
          pela Calriz Sistemas Ltda., com sede na Av. Paulista, 2073, São Paulo/SP, CEP 01311-940 ("Calriz"). Ao marcar
          a opção de aceite no formulário de inscrição, o participante declara que leu, entendeu e concorda com este
          Regulamento, com os <Link to="/termos-de-uso">Termos de Uso</Link> e com a{" "}
          <Link to="/politica-de-privacidade">Política de Privacidade</Link> do site.
        </p>
      }
      sections={SECTIONS}
      updated="Última atualização: novembro de 2026"
    />
  );
}
